import { useId, type FormEvent } from "react";
import type { Category, CategoryInput } from "@/api/finance";
import { RequestError } from "@/components/RequestError";
import { SelectField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

type CategoryFormProps = {
  initial: Category | null;
  buckets: { id: string; name: string }[];
  /** The expense accounts of the chart; blank = a new account named after the category. */
  ledgerAccounts?: { id: string; code: string; name: string }[];
  nextSortOrder: number;
  submitLabel: string;
  onSubmit: (input: CategoryInput) => Promise<unknown>;
  onCancel: () => void;
  onReload?: () => void;
};

export function CategoryForm({
  initial,
  buckets,
  ledgerAccounts,
  nextSortOrder,
  submitLabel,
  onSubmit,
  onCancel,
  onReload,
}: CategoryFormProps) {
  const id = useId();
  const { values, set } = useFormValues({
    name: initial?.name ?? "",
    default_bucket_id: initial?.default_bucket_id ?? "",
    sort_order: String(initial?.sort_order ?? nextSortOrder),
    ledger_account_id: initial?.ledger_account_id ?? "",
  });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(() =>
      onSubmit({
        name: values.name.trim(),
        default_bucket_id: values.default_bucket_id,
        sort_order: Number(values.sort_order) || 0,
        archived: initial?.archived ?? false,
        ledger_account_id: values.ledger_account_id || null,
      }),
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <RequestError error={error} onReload={onReload} />
      <div className="grid gap-4 md:grid-cols-3">
        <TextField
          id={`${id}-name`}
          label="Name"
          value={values.name}
          onChange={(value) => set("name", value)}
          error={errors.name}
        />
        <SelectField
          id={`${id}-bucket`}
          label="Default bucket"
          value={values.default_bucket_id}
          onChange={(value) => set("default_bucket_id", value)}
          options={buckets.map((bucket) => ({ value: bucket.id, label: bucket.name }))}
          placeholder="Choose a bucket"
          error={errors.default_bucket_id}
          hint="What a bill of this kind draws from, unless the bill says otherwise"
        />
        <TextField
          id={`${id}-order`}
          label="Order"
          inputMode="numeric"
          value={values.sort_order}
          onChange={(value) => set("sort_order", value)}
          error={errors.sort_order}
        />
        {ledgerAccounts && (
          <SelectField
            id={`${id}-ledger`}
            label="Ledger account"
            value={values.ledger_account_id}
            onChange={(value) => set("ledger_account_id", value)}
            options={ledgerAccounts.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }))}
            placeholder={initial ? "Keep the current account" : "New account named after the category"}
            error={errors.ledger_account_id}
            hint="Where this category's bills are charged in the books"
          />
        )}
      </div>
      <FormActions
        saving={saving}
        submitLabel={submitLabel}
        onCancel={onCancel}
        disabled={!values.default_bucket_id}
      />
    </form>
  );
}
